import { ArrowRightIcon, InfoIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react'
import { ReactElement, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import {
  MAX_PHRASE_LENGTH,
  MAX_PHRASE_RULES,
  PHRASE_RULE_CHANNELS,
  PhraseRule,
  cleanPhraseRules,
  phraseRulesDiffer,
  phraseRulesProblem,
} from 'app/lib/aiAgentPhraseRules'
import { useAIAgentStore } from 'app/stores/StoreContext'
import { useSettingsSection } from './useSettingsSave'

const HINT = `Heard your agent say something you would rather it didn't? Add it here with what to say instead, and pick whether it applies on calls, texts, or both.`

/**
 * The "Say this, not that" part of Scripting, edited in place like the rest of Settings: one row
 * per rule with the phrase to stop, the phrase to use, and where it applies. Saves through the
 * page's save bar.
 */
export function AiAgentPhraseRules({ locationId, rules }: { locationId: string; rules: PhraseRule[] }): ReactElement {
  const agentStore = useAIAgentStore()
  const [draft, setDraft] = useState<PhraseRule[] | null>(null)
  const shown = draft ?? rules
  const filled = cleanPhraseRules(shown).length
  const update = (index: number, rule: PhraseRule) => setDraft(shown.map((r, i) => (i === index ? rule : r)))

  useSettingsSection(`phraseRules`, {
    dirty: !!draft && phraseRulesDiffer(draft, rules),
    blocked: draft ? phraseRulesProblem(draft) : undefined,
    save: () => agentStore.updateConfig(locationId, { sayThisNotThatRules: cleanPhraseRules(shown) }),
    discard: () => setDraft(null),
  })

  return (
    <div className="flex flex-col gap-3 border-t border-default pt-5" data-testid="phrase-rules">
      <div className="flex min-h-7 flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <span className="flex items-center gap-2 whitespace-nowrap text-label-sm text-default">
          Say This, Not That
          <TooltipWrapper message={HINT} placement="bottom-start" tooltipClassName="max-w-xs">
            <span className="flex cursor-help items-center text-subtle" aria-label="About Say This, Not That">
              <InfoIcon className="h-4 w-4" />
            </span>
          </TooltipWrapper>
        </span>
        <span className="text-body-xs text-subtle" data-testid="phrase-rules-limits">
          {filled} of {MAX_PHRASE_RULES} phrases · up to {MAX_PHRASE_LENGTH} characters each
        </span>
      </div>

      {shown.length === 0 && (
        <span className="text-body-sm text-subtle">Nothing yet. Add a phrase your agent should say differently.</span>
      )}

      {shown.map((rule, index) => (
        <div
          key={index}
          className="flex flex-wrap items-center gap-2 border-b border-default pb-3 md:flex-nowrap md:border-b-0 md:pb-0"
          data-testid={`phrase-rule-${index}`}
        >
          <Input
            className="min-w-0 flex-1 basis-full md:basis-0"
            aria-label="Instead of"
            placeholder="Instead of, e.g. “Unfortunately”"
            maxLength={MAX_PHRASE_LENGTH}
            value={rule.stopPhrase}
            onChange={(event) => update(index, { ...rule, stopPhrase: event.target.value })}
            data-testid={`phrase-rule-stop-${index}`}
          />
          <ArrowRightIcon className="hidden h-4 w-4 shrink-0 text-subtle md:block" aria-hidden="true" />
          <Input
            className="min-w-0 flex-1 basis-full md:basis-0"
            aria-label="Say"
            placeholder="Say, e.g. “Good news”"
            maxLength={MAX_PHRASE_LENGTH}
            value={rule.replacementPhrase}
            onChange={(event) => update(index, { ...rule, replacementPhrase: event.target.value })}
            data-testid={`phrase-rule-replacement-${index}`}
          />
          <Select value={rule.channel} onValueChange={(channel) => update(index, { ...rule, channel })}>
            <SelectTrigger
              aria-label="Applies on"
              className="h-9 flex-1 md:w-40 md:flex-none"
              data-testid={`phrase-rule-channel-${index}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PHRASE_RULE_CHANNELS.map((channel) => (
                <SelectItem key={channel.value} value={channel.value}>
                  {channel.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="white"
            size="icon"
            aria-label="Remove phrase"
            onClick={() => setDraft(shown.filter((_, i) => i !== index))}
            data-testid={`phrase-rule-remove-${index}`}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="white"
        size="sm"
        className="self-start"
        disabled={shown.length >= MAX_PHRASE_RULES}
        onClick={() => setDraft([...shown, { stopPhrase: ``, replacementPhrase: ``, channel: `both` }])}
        data-testid="phrase-rule-add"
      >
        <PlusIcon className="h-4 w-4" />
        Add phrase
      </Button>
    </div>
  )
}
